import React from "react";
import { useParams } from "react-router-dom";
import {
	CircularProgress,
	Container,
	Paper,
	Typography,
	Box,
	Alert,
} from "@mui/material";

import { BoothProvider } from "../contexts/BoothContext";
import { useBoothData } from "../hooks/useBoothData";
import WalkUpBookingSection from "../components/booking/WalkUpBookingSection";
import ReservationBookingSection from "../components/booking/ReservationBookingSection";

function BoothPageContent() {
	const { boothId } = useParams();
	const { booth, loading, error, isReady } = useBoothData(boothId);

	if (loading) {
		return (
			<Container maxWidth="sm">
				<Box
					sx={{
						minHeight: "100vh",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexDirection: "column",
						gap: 2,
					}}
				>
					<CircularProgress size={60} />
					<Typography variant="h6" color="text.secondary">
						Loading booth information...
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Booth ID: {boothId}
					</Typography>
				</Box>
			</Container>
		);
	}

	if (error) {
		return (
			<Container maxWidth="sm">
				<Box
					sx={{
						minHeight: "100vh",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<Paper elevation={3} sx={{ p: 4, width: "100%" }}>
						<Alert severity="error" sx={{ mb: 2 }}>
							{error}
						</Alert>
						<Typography variant="h5" gutterBottom>
							Unable to Load Booth
						</Typography>
						<Typography variant="body1" color="text.secondary">
							We couldn't find information for booth {boothId}. Please try
							scanning the QR code again or contact support.
						</Typography>
						<Box sx={{ mt: 3, textAlign: "center" }}>
							<Typography variant="body2" color="text.secondary">
								Booth ID: {boothId}
							</Typography>
						</Box>
					</Paper>
				</Box>
			</Container>
		);
	}

	if (!isReady) {
		return (
			<Container maxWidth="sm">
				<Box
					sx={{
						minHeight: "100vh",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<Typography variant="h6" color="text.secondary">
						No booth data available
					</Typography>
				</Box>
			</Container>
		);
	}

	return (
		<BoothProvider booth={booth}>
			<Container maxWidth="md">
				<Box sx={{ py: 3 }}>
					{/* Conditional booking sections based on booth type */}
					{booth.type === "paid-byMinute" && <WalkUpBookingSection />}
					{booth.type === "reservation" && <ReservationBookingSection />}

					{/* Fallback for unknown booth types */}
					{!["paid-byMinute", "reservation"].includes(booth.type) && (
						<Alert severity="warning" sx={{ mt: 2 }}>
							Unknown booth type: {booth.type}. Please contact support.
						</Alert>
					)}
				</Box>
			</Container>
		</BoothProvider>
	);
}

export default function BoothPage() {
	return <BoothPageContent />;
}
